import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "row",
    "column",
    "placeNameInput",
    "msoNameInput",
    "stateInput",
    "taskerNameInput",
    "daysAgoInput",
    "productNameInput",
    "brandInput",
    "ph1Select",
    "ph2Select",
    "ph3Select",
    "coreCheckbox",
    "focusCheckbox",
    "phoneFocusCheckbox",
    "npdCheckbox",
    "deletedCheckbox",
    "xCheckbox",
    "hasRangedCheckbox",
    "hasCutInCheckbox",
    "hasOutOfStockCheckbox",
    "spinner",
    "waffleContent",
    // Statistics card targets for updating counts and percentages
    "rangedCount",
    "rangedPercentage",
    "rangedStoreCount",
    "rangedStoreTotal",
    "rangedStorePercentage",
    "cutInCount",
    "cutInPercentage",
    "cutInStoreCount",
    "cutInStoreTotal",
    "cutInStorePercentage",
    "outOfStockCount",
    "outOfStockPercentage",
    "outOfStockStoreCount",
    "outOfStockStoreTotal",
    "inStockRate",
    "distributionPercentage",
    "distributionCount",
    "distributionTotal",
    "fullyRangedStoreCount",
    "fullyRangedStoreTotal",
    "fullyRangedPercentage"
  ]

  static values = {
    ph1Data: Array,
    ph2Data: Array,
    ph3Data: Array,
    clientId: Number
  }

  connect() {
    // Load PH hierarchy data on connect
    this.loadPhHierarchyData().catch(error => {
      console.error("Error loading PH hierarchy data:", error)
    })

    // Set up standard DOM event listeners for cascading after SlimSelect initialization
    setTimeout(() => this.setupCascadingEvents(), 500)
  }

  async loadPhHierarchyData() {
    try {
      // Fetch all PH data - scoped by client and use special parameter to get ALL data regardless of parent_id
      const clientParam = this.hasClientIdValue ? `&client_id=${this.clientIdValue}` : '';
      const [ph1Response, ph2Response, ph3Response] = await Promise.all([
        fetch(`/api/v1/product_hierarchy_nodes?level=ph1${clientParam}`),
        fetch(`/api/v1/product_hierarchy_nodes?level=ph2&all=true${clientParam}`),
        fetch(`/api/v1/product_hierarchy_nodes?level=ph3&all=true${clientParam}`)
      ])

      this.ph1DataValue = await ph1Response.json()
      this.ph2DataValue = await ph2Response.json()
      this.ph3DataValue = await ph3Response.json()

      // Populate initial dropdowns
      this.populatePh1Options()
      this.populatePh2Options([]) // Empty initially - will populate when PH1 selected
      this.populatePh3Options([]) // Empty initially - will populate when PH2 selected

    } catch (error) {
      console.error('Error loading PH hierarchy data:', error)
    }
  }

  populatePh1Options() {
    if (!this.hasPh1SelectTarget || !this.ph1DataValue) return

    // Prepare data for SlimSelect format
    const slimSelectData = [
      { placeholder: true, text: 'Select PH1...', value: '' }
    ]

    // Add PH1 options
    this.ph1DataValue.forEach(ph1 => {
      slimSelectData.push({
        text: ph1.name,
        value: ph1.id.toString()
      })
    })

    // Use SlimSelect's setData method
    this.setSlimSelectData(this.ph1SelectTarget, slimSelectData)
  }

  populatePh2Options(selectedPh1Ids) {
    if (!this.hasPh2SelectTarget || !this.ph2DataValue) return

    // Prepare data for SlimSelect format
    const slimSelectData = [
      { placeholder: true, text: 'Select PH2...', value: '' }
    ]

    // Filter PH2 options based on selected PH1s
    const filteredPh2s = selectedPh1Ids.length === 0 ? [] :
      this.ph2DataValue.filter(ph2 => selectedPh1Ids.includes(ph2.parent_id.toString()))

    // Add filtered PH2 options
    filteredPh2s.forEach(ph2 => {
      slimSelectData.push({
        text: ph2.name,
        value: ph2.id.toString()
      })
    })

    // Use SlimSelect's setData method
    this.setSlimSelectData(this.ph2SelectTarget, slimSelectData)
  }

  populatePh3Options(selectedPh2Ids) {
    if (!this.hasPh3SelectTarget || !this.ph3DataValue) return

    // Prepare data for SlimSelect format
    const slimSelectData = [
      { placeholder: true, text: 'Select PH3...', value: '' }
    ]

    // Filter PH3 options based on selected PH2s
    const filteredPh3s = selectedPh2Ids.length === 0 ? [] :
      this.ph3DataValue.filter(ph3 => selectedPh2Ids.includes(ph3.parent_id.toString()))

    // Add filtered PH3 options
    filteredPh3s.forEach(ph3 => {
      slimSelectData.push({
        text: ph3.name,
        value: ph3.id.toString()
      })
    })

    // Use SlimSelect's setData method
    this.setSlimSelectData(this.ph3SelectTarget, slimSelectData)
  }

  setSlimSelectData(selectElement, data) {
    // Find the associated SlimSelect controller
    const slimController = this.application.getControllerForElementAndIdentifier(selectElement, 'slim')

    if (slimController && slimController.select) {
      // Use SlimSelect's setData method to update options
      slimController.select.setData(data)
    } else {
      // Fallback: update the raw select options
      selectElement.innerHTML = ''
      data.forEach(option => {
        const optionElement = document.createElement('option')
        optionElement.value = option.value
        optionElement.textContent = option.text
        if (option.placeholder) optionElement.disabled = true
        selectElement.appendChild(optionElement)
      })
    }
  }

  setupCascadingEvents() {
    // Set up PH1 change event
    if (this.hasPh1SelectTarget) {
      this.ph1SelectTarget.addEventListener('change', () => {
        this.filterPh2()
      })
    }

    // Set up PH2 change event
    if (this.hasPh2SelectTarget) {
      this.ph2SelectTarget.addEventListener('change', () => {
        this.filterPh3()
      })
    }

    // Set up Enter key handling for all PH dropdowns
    this.setupEnterKeyHandling()

    // Initial filter
    this.filterPh2()
    this.filterPh3()
  }

  setupEnterKeyHandling() {
    [this.ph1SelectTarget, this.ph2SelectTarget, this.ph3SelectTarget].forEach(target => {
      if (target) {
        target.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.keyCode === 13) {
            e.preventDefault()
            this.filter()
          }
        })
      }
    })
  }

  filterPh2() {
    if (!this.hasPh2SelectTarget || !this.ph2DataValue) return

    // Try both Stimulus target and direct DOM access
    const ph1Select = this.ph1SelectTarget || document.getElementById('ph1_filter')

    if (!ph1Select) return

    // Use the same pattern as setSlimSelectData to find SlimSelect controller
    const slimPh1Controller = this.application.getControllerForElementAndIdentifier(ph1Select, 'slim')

    if (!slimPh1Controller || !slimPh1Controller.select) return

    const selectedValues = slimPh1Controller.select.getSelected()
    const selectedPh1Ids = selectedValues.map(val => parseInt(val)).filter(v => !isNaN(v))

    // Get current PH2 selections using the same consistent pattern
    const slimPh2Controller = this.application.getControllerForElementAndIdentifier(this.ph2SelectTarget, 'slim')
    const currentPh2Selections = slimPh2Controller?.select ?
      slimPh2Controller.select.getSelected().map(val => parseInt(val)).filter(v => !isNaN(v)) : []

    let filteredPh2Options
    if (selectedPh1Ids.length === 0) {
      // No PH1 selected, clear PH2
      filteredPh2Options = []
    } else {
      // Filter PH2 to only show those whose parent is in selected PH1
      filteredPh2Options = this.ph2DataValue.filter(node => {
        return selectedPh1Ids.includes(node.parent_id)
      })
    }

    // Update SlimSelect with filtered options
    if (slimPh2Controller && slimPh2Controller.select) {
      const options = [
        { placeholder: true, text: 'Select PH2...', value: '' },
        ...filteredPh2Options.map(node => ({
          text: node.name,
          value: node.id.toString()
        }))
      ]

      slimPh2Controller.select.setData(options)

      // Restore selections that are still valid
      const validSelections = currentPh2Selections.filter(id =>
        filteredPh2Options.some(node => node.id === id)
      )
      if (validSelections.length > 0) {
        slimPh2Controller.select.setSelected(validSelections.map(String))
      }
    }

    // After filtering PH2, update PH3
    this.filterPh3()
  }

  filterPh3() {
    if (!this.hasPh3SelectTarget || !this.ph3DataValue) return

    // Use consistent pattern to find SlimSelect controller for PH2
    const slimPh2Controller = this.application.getControllerForElementAndIdentifier(this.ph2SelectTarget, 'slim')

    if (!slimPh2Controller || !slimPh2Controller.select) return

    const selectedPh2Ids = slimPh2Controller.select.getSelected().map(val => parseInt(val)).filter(v => !isNaN(v))

    // Get current PH3 selections using the same consistent pattern
    const slimPh3Controller = this.application.getControllerForElementAndIdentifier(this.ph3SelectTarget, 'slim')
    const currentPh3Selections = slimPh3Controller?.select ?
      slimPh3Controller.select.getSelected().map(val => parseInt(val)).filter(v => !isNaN(v)) : []

    let filteredPh3Options
    if (selectedPh2Ids.length === 0) {
      // No PH2 selected, clear PH3
      filteredPh3Options = []
    } else {
      // Filter PH3 to only show those whose parent is in selected PH2
      filteredPh3Options = this.ph3DataValue.filter(node => {
        return selectedPh2Ids.includes(node.parent_id)
      })
    }

    // Update SlimSelect with filtered options
    if (slimPh3Controller && slimPh3Controller.select) {
      const options = [
        { placeholder: true, text: 'Select PH3...', value: '' },
        ...filteredPh3Options.map(node => ({
          text: node.name,
          value: node.id.toString()
        }))
      ]

      slimPh3Controller.select.setData(options)

      // Restore selections that are still valid
      const validSelections = currentPh3Selections.filter(id =>
        filteredPh3Options.some(node => node.id === id)
      )
      if (validSelections.length > 0) {
        slimPh3Controller.select.setSelected(validSelections.map(String))
      }
    }
  }

  filter() {
    // Set global flag for testing
    window.filterMethodCalled = true
    window.filterCallCount = (window.filterCallCount || 0) + 1

    try {
      // Show spinner immediately
      if (this.hasSpinnerTarget) {
        this.spinnerTarget.classList.remove('hidden')
      }

      // Use requestAnimationFrame to ensure DOM updates before heavy processing
      requestAnimationFrame(() => {
        try {
          // Call the actual filter logic
          this.performFilter()
          window.timeoutExecuted = true
        } catch (error) {
          console.error("Error during filtering:", error)
          window.filterError = error.message
          this.forceHideSpinner()
        }
      })
    } catch (error) {
      window.filterError = error.message
      console.error("Filter error:", error)
      this.forceHideSpinner()
    }
  }

  forceHideSpinner() {
    // Always hide spinner no matter what
    document.querySelectorAll('[data-distribution-matrix-filter-target="spinner"]').forEach(el => el.classList.add('hidden'))
    document.querySelectorAll('[data-distribution-matrix-filter-target="waffleContent"]').forEach(el => {
      el.classList.remove('opacity-50', 'pointer-events-none')
    })
  }

  hideSpinner() {
    try {
      if (this.hasSpinnerTarget) {
        this.spinnerTarget.classList.add('hidden')
      } else {
        // Fallback: find spinner manually
        const spinner = document.querySelector('[data-distribution-matrix-filter-target="spinner"]')
        if (spinner) {
          spinner.classList.add('hidden')
        }
      }

      if (this.hasWaffleContentTarget) {
        this.waffleContentTarget.classList.remove('opacity-50', 'pointer-events-none')
      } else {
        // Fallback: find content manually
        const content = document.querySelector('[data-distribution-matrix-filter-target="waffleContent"]')
        if (content) {
          content.classList.remove('opacity-50', 'pointer-events-none')
        }
      }
    } catch (error) {
      console.error("Error in hideSpinner:", error)
      // Emergency cleanup
      this.forceHideSpinner()
    }
  }

  performFilterSimple() {
    // Get basic filter values with safety checks
    const placeNameFilter = this.hasPlaceNameInputTarget ? this.placeNameInputTarget.value.toLowerCase().trim() : ""
    const productNameFilter = this.hasProductNameInputTarget ? this.productNameInputTarget.value.toLowerCase().trim() : ""

    // Hide spinner when done
    this.forceHideSpinner()
  }

  performFilter() {

    // Place filters - with safety checks
    const placeNameFilter = this.hasPlaceNameInputTarget ? this.placeNameInputTarget.value.toLowerCase().trim() : ""
    const msoNameFilter = this.hasMsoNameInputTarget ? this.msoNameInputTarget.value.toLowerCase().trim() : ""
    const stateFilter = this.hasStateInputTarget ? this.stateInputTarget.value.toLowerCase().trim() : ""
    const taskerNameFilter = this.hasTaskerNameInputTarget ? this.taskerNameInputTarget.value.toLowerCase().trim() : ""
    const daysAgoFilter = this.hasDaysAgoInputTarget ? this.daysAgoInputTarget.value.trim() : ""

    // Product filters - with safety checks
    const productNameFilter = this.hasProductNameInputTarget ? this.productNameInputTarget.value.toLowerCase().trim() : ""
    const brandFilter = this.hasBrandInputTarget ? this.brandInputTarget.value.toLowerCase().trim() : ""

    // Get selected PH values as arrays of IDs using consistent SlimSelect controller access
    const ph1Controller = this.hasPh1SelectTarget ?
      this.application.getControllerForElementAndIdentifier(this.ph1SelectTarget, 'slim') : null
    const ph2Controller = this.hasPh2SelectTarget ?
      this.application.getControllerForElementAndIdentifier(this.ph2SelectTarget, 'slim') : null
    const ph3Controller = this.hasPh3SelectTarget ?
      this.application.getControllerForElementAndIdentifier(this.ph3SelectTarget, 'slim') : null

    const ph1Filter = ph1Controller?.select ?
      ph1Controller.select.getSelected().filter(value => value !== '') : []
    const ph2Filter = ph2Controller?.select ?
      ph2Controller.select.getSelected().filter(value => value !== '') : []
    const ph3Filter = ph3Controller?.select ?
      ph3Controller.select.getSelected().filter(value => value !== '') : []

    const coreFilter = this.hasCoreCheckboxTarget ? this.coreCheckboxTarget.checked : false
    const focusFilter = this.hasFocusCheckboxTarget ? this.focusCheckboxTarget.checked : false
    const phoneFocusFilter = this.hasPhoneFocusCheckboxTarget ? this.phoneFocusCheckboxTarget.checked : false
    const npdFilter = this.hasNpdCheckboxTarget ? this.npdCheckboxTarget.checked : false
    const hideDeletedFilter = this.hasDeletedCheckboxTarget ? this.deletedCheckboxTarget.checked : false
    const xFilter = this.hasXCheckboxTarget ? this.xCheckboxTarget.checked : false

    // Store status filters - with safety checks
    const hasRangedFilter = this.hasHasRangedCheckboxTarget ? this.hasRangedCheckboxTarget.checked : false
    const hasCutInFilter = this.hasHasCutInCheckboxTarget ? this.hasCutInCheckboxTarget.checked : false
    const hasOutOfStockFilter = this.hasHasOutOfStockCheckboxTarget ? this.hasOutOfStockCheckboxTarget.checked : false

    let visibleRowCount = 0
    let visibleColumnCount = 0

    // Calculate timestamp for days ago filter
    let daysAgoTimestamp = null
    if (daysAgoFilter) {
      const daysAgo = parseInt(daysAgoFilter)
      if (!isNaN(daysAgo) && daysAgo >= 0) {
        const now = new Date()
        const targetDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000))
        daysAgoTimestamp = Math.floor(targetDate.getTime() / 1000)
      }
    }

    // Filter rows (places)
    this.rowTargets.forEach(row => {
      const placeName = row.dataset.placeName.toLowerCase()
      const msoName = row.dataset.msoName.toLowerCase()
      const state = row.dataset.state.toLowerCase()
      const taskerName = (row.dataset.taskerName || "").toLowerCase()
      const lastVisitDate = row.dataset.lastVisitDate ? parseInt(row.dataset.lastVisitDate) : null
      const storeId = row.dataset.placeId

      const matchesPlaceName = !placeNameFilter || placeName.includes(placeNameFilter)
      const matchesMsoName = !msoNameFilter || msoName.includes(msoNameFilter)
      const matchesState = !stateFilter || state.includes(stateFilter)
      const matchesTaskerName = !taskerNameFilter || taskerName.includes(taskerNameFilter)

      // Days ago filter: show rows where last visit is LESS THAN OR EQUAL TO days ago
      const matchesDaysAgo = !daysAgoTimestamp || (lastVisitDate && lastVisitDate >= daysAgoTimestamp)

      // Check if store has required statuses (only for visible products)
      let matchesStatusFilters = true
      if (hasRangedFilter || hasCutInFilter || hasOutOfStockFilter) {
        let storeHasRanged = false
        let storeHasCutIn = false
        let storeHasOutOfStock = false

        // Get visible columns to check statuses
        const visibleColumns = this.columnTargets.filter(col => col.style.display !== "none")

        visibleColumns.forEach(col => {
          const sku = col.dataset.sku
          const cellWrapper = row.querySelector(`[data-product-sku="${sku}"]`)

          if (cellWrapper) {
            const statusSquare = cellWrapper.querySelector('[data-status]')
            if (statusSquare) {
              const status = statusSquare.dataset.status
              if (status === "ranged") storeHasRanged = true
              else if (status === "cut_in") storeHasCutIn = true
              else if (status === "out_of_stock") storeHasOutOfStock = true
            }
          }
        })

        // Apply status filters: when checked, show ONLY stores with that status
        const matchesHasRanged = !hasRangedFilter || storeHasRanged
        const matchesHasCutIn = !hasCutInFilter || storeHasCutIn
        const matchesHasOutOfStock = !hasOutOfStockFilter || storeHasOutOfStock

        matchesStatusFilters = matchesHasRanged && matchesHasCutIn && matchesHasOutOfStock
      }

      if (matchesPlaceName && matchesMsoName && matchesState && matchesTaskerName && matchesDaysAgo && matchesStatusFilters) {
        row.style.display = ""
        visibleRowCount++
      } else {
        row.style.display = "none"
      }
    })

    // Filter columns (products)
    this.columnTargets.forEach(column => {
      const productName = column.dataset.productName.toLowerCase()
      const brand = column.dataset.brand.toLowerCase()

      // Get PH hierarchy data for this column - these should be IDs from the data attributes
      const columnPh1Id = column.dataset.ph1Id || ""
      const columnPh2Id = column.dataset.ph2Id || ""
      const columnPh3Id = column.dataset.ph3Id || ""

      const isCore = column.dataset.core === "true"
      const isFocus = column.dataset.focus === "true"
      const isPhoneFocus = column.dataset.phoneFocus === "true"
      const isNpd = column.dataset.npd === "true"
      const isDeleted = column.dataset.deleted === "true"
      const sku = column.dataset.sku

      const matchesProductName = !productNameFilter || productName.includes(productNameFilter)
      const matchesBrand = !brandFilter || brand.includes(brandFilter)

      // PH filters: check if column's PH ID is in the selected array (or no filter applied)
      const matchesPh1 = ph1Filter.length === 0 || ph1Filter.includes(columnPh1Id)
      const matchesPh2 = ph2Filter.length === 0 || ph2Filter.includes(columnPh2Id)
      const matchesPh3 = ph3Filter.length === 0 || ph3Filter.includes(columnPh3Id)

      // Checkbox filters: when checked, show ONLY products with that attribute
      const matchesCore = !coreFilter || isCore
      const matchesFocus = !focusFilter || isFocus
      const matchesPhoneFocus = !phoneFocusFilter || isPhoneFocus
      const matchesNpd = !npdFilter || isNpd
      const matchesDeleted = !hideDeletedFilter || !isDeleted

      // X filter: when checked, show only products that have at least one "not_ranged" status
      let matchesX = true
      if (xFilter) {
        // Check if this product has any stores with "not_ranged" status
        const visibleRows = this.rowTargets.filter(row => row.style.display !== "none")
        let hasNotRanged = false

        visibleRows.forEach(row => {
          const cellWrapper = row.querySelector(`[data-product-sku="${sku}"]`)
          if (cellWrapper) {
            const statusSquare = cellWrapper.querySelector('[data-status]')
            if (statusSquare && statusSquare.dataset.status === "not_ranged") {
              hasNotRanged = true
            }
          }
        })

        matchesX = hasNotRanged
      }

      const isVisible = matchesProductName && matchesBrand && matchesPh1 && matchesPh2 && matchesPh3 && matchesCore && matchesFocus && matchesPhoneFocus && matchesNpd && matchesDeleted && matchesX

      if (isVisible) {
        column.style.display = ""
        visibleColumnCount++
      } else {
        column.style.display = "none"
      }

      // Also hide/show all cells with this product SKU
      const cells = this.element.querySelectorAll(`[data-product-sku="${sku}"]`)
      cells.forEach(cell => {
        cell.style.display = isVisible ? "" : "none"
      })
    })

    // Update statistics based on visible data
    this.updateStatistics()
    this.updateIceCreamGraphs()

    // Hide spinner and restore content
    this.hideSpinner()
  }

  updateStatistics() {
    // Get all visible rows (stores)
    const visibleRows = this.rowTargets.filter(row => row.style.display !== "none")
    const visibleColumns = this.columnTargets.filter(col => col.style.display !== "none")

    // Get unique visible store IDs
    const visibleStoreIds = new Set(visibleRows.map(row => row.dataset.placeId))
    const totalVisibleStores = visibleStoreIds.size

    // Count cells by status for visible products and stores
    let totalRanged = 0
    let totalCutIn = 0
    let totalOutOfStock = 0
    let totalNotRanged = 0

    // Store sets for tracking which stores have each status
    const storesWithRanged = new Set()
    const storesWithCutIn = new Set()
    const storesWithOutOfStock = new Set()

    // For each visible row (store)
    visibleRows.forEach(row => {
      const storeId = row.dataset.placeId
      let storeHasRanged = false
      let storeHasCutIn = false
      let storeHasOutOfStock = false
      let storeHasNotRanged = false

      // For each visible column (product)
      visibleColumns.forEach(col => {
        const sku = col.dataset.sku
        // Find the cell wrapper for this store/product combination
        const cellWrapper = row.querySelector(`[data-product-sku="${sku}"]`)

        if (cellWrapper && cellWrapper.style.display !== "none") {
          // Find the actual status square inside the wrapper
          const statusSquare = cellWrapper.querySelector('[data-status]')

          if (statusSquare) {
            const status = statusSquare.dataset.status

            if (status === "ranged") {
              totalRanged++
              storeHasRanged = true
            } else if (status === "cut_in") {
              totalCutIn++
              storeHasCutIn = true
            } else if (status === "out_of_stock") {
              totalOutOfStock++
              storeHasOutOfStock = true
            } else if (status === "not_ranged") {
              totalNotRanged++
              storeHasNotRanged = true
            }
          }
        }
      })

      if (storeHasRanged) storesWithRanged.add(storeId)
      if (storeHasCutIn) storesWithCutIn.add(storeId)
      if (storeHasOutOfStock) storesWithOutOfStock.add(storeId)
    })

    // Calculate fully ranged stores (stores with no not_ranged products in visible set)
    const fullyRangedStores = new Set()
    visibleRows.forEach(row => {
      const storeId = row.dataset.placeId
      let hasNotRanged = false

      visibleColumns.forEach(col => {
        const sku = col.dataset.sku
        const cellWrapper = row.querySelector(`[data-product-sku="${sku}"]`)

        if (cellWrapper && cellWrapper.style.display !== "none") {
          const statusSquare = cellWrapper.querySelector('[data-status]')
          if (statusSquare && statusSquare.dataset.status === "not_ranged") {
            hasNotRanged = true
          }
        }
      })

      if (!hasNotRanged && visibleColumns.length > 0) {
        fullyRangedStores.add(storeId)
      }
    })

    // Calculate totals and percentages
    const totalDistribution = totalRanged + totalCutIn + totalOutOfStock
    const totalPossible = totalDistribution + totalNotRanged

    const rangedPercentage = totalPossible > 0 ? ((totalRanged / totalPossible) * 100).toFixed(1) : 0
    const cutInPercentage = totalPossible > 0 ? ((totalCutIn / totalPossible) * 100).toFixed(1) : 0
    const outOfStockPercentage = totalPossible > 0 ? ((totalOutOfStock / totalPossible) * 100).toFixed(1) : 0
    const distributionPercentage = totalPossible > 0 ? ((totalDistribution / totalPossible) * 100).toFixed(1) : 0
    const inStockRate = (100 - parseFloat(outOfStockPercentage)).toFixed(1)

    const storesRangedPercentage = totalVisibleStores > 0 ? ((storesWithRanged.size / totalVisibleStores) * 100).toFixed(1) : 0
    const storesCutInPercentage = totalVisibleStores > 0 ? ((storesWithCutIn.size / totalVisibleStores) * 100).toFixed(1) : 0
    const fullyRangedPercentage = totalVisibleStores > 0 ? ((fullyRangedStores.size / totalVisibleStores) * 100).toFixed(1) : 0

    // Update all the card values if targets exist
    if (this.hasRangedCountTarget) this.rangedCountTarget.textContent = totalRanged
    if (this.hasRangedPercentageTarget) this.rangedPercentageTarget.textContent = rangedPercentage + "%"
    if (this.hasRangedStoreCountTarget) this.rangedStoreCountTarget.textContent = storesWithRanged.size
    if (this.hasRangedStoreTotalTarget) this.rangedStoreTotalTarget.textContent = totalVisibleStores
    if (this.hasRangedStorePercentageTarget) this.rangedStorePercentageTarget.textContent = storesRangedPercentage + "%"

    if (this.hasCutInCountTarget) this.cutInCountTarget.textContent = totalCutIn
    if (this.hasCutInPercentageTarget) this.cutInPercentageTarget.textContent = cutInPercentage + "%"
    if (this.hasCutInStoreCountTarget) this.cutInStoreCountTarget.textContent = storesWithCutIn.size
    if (this.hasCutInStoreTotalTarget) this.cutInStoreTotalTarget.textContent = totalVisibleStores
    if (this.hasCutInStorePercentageTarget) this.cutInStorePercentageTarget.textContent = storesCutInPercentage + "%"

    if (this.hasOutOfStockCountTarget) this.outOfStockCountTarget.textContent = totalOutOfStock
    if (this.hasOutOfStockPercentageTarget) this.outOfStockPercentageTarget.textContent = outOfStockPercentage + "%"
    if (this.hasOutOfStockStoreCountTarget) this.outOfStockStoreCountTarget.textContent = storesWithOutOfStock.size
    if (this.hasOutOfStockStoreTotalTarget) this.outOfStockStoreTotalTarget.textContent = totalVisibleStores
    if (this.hasInStockRateTarget) this.inStockRateTarget.textContent = inStockRate + "%"

    if (this.hasDistributionPercentageTarget) this.distributionPercentageTarget.textContent = distributionPercentage + "%"
    if (this.hasDistributionCountTarget) this.distributionCountTarget.textContent = totalDistribution
    if (this.hasDistributionTotalTarget) this.distributionTotalTarget.textContent = totalPossible
    if (this.hasFullyRangedStoreCountTarget) this.fullyRangedStoreCountTarget.textContent = fullyRangedStores.size
    if (this.hasFullyRangedStoreTotalTarget) this.fullyRangedStoreTotalTarget.textContent = totalVisibleStores
    if (this.hasFullyRangedPercentageTarget) this.fullyRangedPercentageTarget.textContent = fullyRangedPercentage + "%"

  }

  updateIceCreamGraphs() {
    // Get all visible rows (stores)
    const visibleRows = this.rowTargets.filter(row => row.style.display !== "none")
    const visibleColumns = this.columnTargets.filter(col => col.style.display !== "none")

    // For each visible row, recalculate its ice cream graph based on visible products only
    visibleRows.forEach(row => {
      const storeId = row.dataset.placeId

      // Count statuses for this store across visible products
      let storeRanged = 0
      let storeCutIn = 0
      let storeOutOfStock = 0
      let storeNotRanged = 0

      visibleColumns.forEach(col => {
        const sku = col.dataset.sku
        const cellWrapper = row.querySelector(`[data-product-sku="${sku}"]`)

        if (cellWrapper && cellWrapper.style.display !== "none") {
          const statusSquare = cellWrapper.querySelector('[data-status]')

          if (statusSquare) {
            const status = statusSquare.dataset.status

            if (status === "ranged") storeRanged++
            else if (status === "cut_in") storeCutIn++
            else if (status === "out_of_stock") storeOutOfStock++
            else if (status === "not_ranged") storeNotRanged++
          }
        }
      })

      // Update the ice cream bars for this store
      const rangedBar = this.element.querySelector(`[data-ice-cream-ranged="${storeId}"]`)
      const cutInBar = this.element.querySelector(`[data-ice-cream-cut-in="${storeId}"]`)
      const outOfStockBar = this.element.querySelector(`[data-ice-cream-out-of-stock="${storeId}"]`)

      if (rangedBar) {
        rangedBar.textContent = storeRanged > 0 ? storeRanged : ""
        rangedBar.title = `Ranged: ${storeRanged}`
      }

      if (cutInBar) {
        cutInBar.textContent = storeCutIn > 0 ? storeCutIn : ""
        cutInBar.title = `Cut In: ${storeCutIn}`
      }

      if (outOfStockBar) {
        outOfStockBar.textContent = storeOutOfStock > 0 ? storeOutOfStock : ""
        outOfStockBar.title = `Out of Stock: ${storeOutOfStock}`
      }

      // Update the battery indicator (distribution percentage)
      const storeDistribution = storeRanged + storeOutOfStock
      const storeTotal = storeDistribution + storeCutIn + storeNotRanged
      const storeDistributionPercentage = storeTotal > 0 ? Math.round((storeDistribution / storeTotal) * 100) : 0

      const batteryBar = this.element.querySelector(`[data-battery-bar="${storeId}"]`)
      const batteryTitle = this.element.querySelector(`[data-battery-title="${storeId}"]`)

      if (batteryBar) {
        batteryBar.style.width = `${storeDistributionPercentage}%`
      }

      if (batteryTitle) {
        batteryTitle.title = `Distribution: ${storeDistribution} / ${storeTotal} (${storeDistributionPercentage}%)`
      }
    })

  }

  clear() {
    try {
      // Clear place filters
      this.placeNameInputTarget.value = ""
      this.msoNameInputTarget.value = ""
      this.stateInputTarget.value = ""
      this.taskerNameInputTarget.value = ""
      this.daysAgoInputTarget.value = ""

      // Clear product filters
      this.productNameInputTarget.value = ""
      this.brandInputTarget.value = ""

      // Clear PH multi-select dropdowns using consistent SlimSelect controller access
      const ph1Controller = this.hasPh1SelectTarget ?
        this.application.getControllerForElementAndIdentifier(this.ph1SelectTarget, 'slim') : null
      const ph2Controller = this.hasPh2SelectTarget ?
        this.application.getControllerForElementAndIdentifier(this.ph2SelectTarget, 'slim') : null
      const ph3Controller = this.hasPh3SelectTarget ?
        this.application.getControllerForElementAndIdentifier(this.ph3SelectTarget, 'slim') : null

      if (ph1Controller?.select) {
        ph1Controller.select.setSelected([])
      }
      if (ph2Controller?.select) {
        ph2Controller.select.setSelected([])
      }
      if (ph3Controller?.select) {
        ph3Controller.select.setSelected([])
      }

      this.coreCheckboxTarget.checked = false
      this.focusCheckboxTarget.checked = false
      if (this.hasPhoneFocusCheckboxTarget) this.phoneFocusCheckboxTarget.checked = false
      this.npdCheckboxTarget.checked = false
      this.deletedCheckboxTarget.checked = false
      this.xCheckboxTarget.checked = false

      // Clear store status filters
      if (this.hasHasRangedCheckboxTarget) {
        this.hasRangedCheckboxTarget.checked = false
      }

      if (this.hasHasCutInCheckboxTarget) {
        this.hasCutInCheckboxTarget.checked = false
      }

      if (this.hasHasOutOfStockCheckboxTarget) {
        this.hasOutOfStockCheckboxTarget.checked = false
      }

      // Reset cascading: update dependent dropdowns
      this.filterPh2()
      this.filterPh3()

      this.filter()
    } catch (error) {
      console.error("Error in clear method:", error)
    }
  }

  handleKeydown(event) {
    // If Enter key is pressed, trigger filtering
    if (event.key === 'Enter' || event.keyCode === 13) {
      event.preventDefault() // Prevent form submission
      this.filter()
    }
  }
}
